import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

interface ShortcutBinding {
  readonly id: string;
  readonly keys: string; // e.g. 'g c', 'Escape', '?'
  readonly action: () => void;
  readonly when?: () => boolean; // Optional guard
}

@Injectable({ providedIn: 'root' })
export class KeyboardShortcutService implements OnDestroy {
  private readonly _zone = inject(NgZone);
  private readonly _bindings: ShortcutBinding[] = [];
  private readonly _subscription: Subscription;

  // Sequence state: tracks first key of a two-key combo (e.g. 'g' in 'g c')
  private _pendingPrefix: string | null = null;
  private _pendingTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Run outside Angular zone to avoid unnecessary change detection
    this._subscription = new Subscription();
    this._zone.runOutsideAngular(() => {
      this._subscription.add(
        fromEvent<KeyboardEvent>(document, 'keydown').subscribe((event) => this.handleKeydown(event))
      );
    });
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    if (this._pendingTimer) clearTimeout(this._pendingTimer);
  }

  public register(binding: ShortcutBinding): void {
    // Remove existing binding with same ID
    const idx = this._bindings.findIndex((b) => b.id === binding.id);
    if (idx !== -1) {
      this._bindings.splice(idx, 1);
    }
    this._bindings.push(binding);
  }

  public unregister(id: string): void {
    const idx = this._bindings.findIndex((b) => b.id === id);
    if (idx !== -1) {
      this._bindings.splice(idx, 1);
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    // Ignore when typing in inputs
    const tag = (event.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if ((event.target as HTMLElement)?.isContentEditable) return;
    // Bindings are plain keys — never hijack browser/system combos like Cmd+A
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const key = event.key.toLowerCase();

    // Check for two-key sequences (e.g. 'g c')
    if (this._pendingPrefix) {
      const combo = `${this._pendingPrefix} ${key}`;
      this.clearPending();

      const binding = this._bindings.find((b) => b.keys.toLowerCase() === combo);
      if (binding && (!binding.when || binding.when())) {
        event.preventDefault();
        this._zone.run(() => binding.action());
        return;
      }
    }

    // Check for single-key bindings
    const singleBinding = this._bindings.find((b) => {
      const bKey = b.keys.toLowerCase();
      // Skip sequence starters (they have a space = two keys)
      if (bKey.includes(' ')) return false;
      return bKey === key;
    });

    if (singleBinding && (!singleBinding.when || singleBinding.when())) {
      event.preventDefault();
      this._zone.run(() => singleBinding.action());
      return;
    }

    // Check if this key is the first key of any sequence
    const isSequenceStart = this._bindings.some((b) => {
      const parts = b.keys.toLowerCase().split(' ');
      return parts.length === 2 && parts[0] === key;
    });

    if (isSequenceStart) {
      this._pendingPrefix = key;
      this._pendingTimer = setTimeout(() => this.clearPending(), 500);
    }
  }

  private clearPending(): void {
    this._pendingPrefix = null;
    if (this._pendingTimer) {
      clearTimeout(this._pendingTimer);
      this._pendingTimer = null;
    }
  }
}
